import React, { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Gift, History, MapPin } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useMember } from "@/lib/member";

const err = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ") || fb;
  return fb;
};

const Field = ({ label, testid, ...rest }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-ink-soft">{label}</span>
    <input data-testid={testid} {...rest}
      className="mt-1.5 h-11 w-full rounded-md border border-input bg-white px-3 text-sm" />
  </label>
);

export default function MemberLogin() {
  const { t } = useI18n();
  const { member, loading, setMember } = useMember();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [f, setF] = useState({
    username: "",
    email: "",
    password: "",
    password_confirmation: "",
    whatsapp: "",
  });
  const [agree, setAgree] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);

  if (loading) return <SiteLayout><div className="mx-auto max-w-md px-5 py-24 text-ink-soft">{t("loading")}</div></SiteLayout>;
  if (member) return <Navigate to="/member" replace />;

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (mode === "signup" && !agree) return toast.error(t("agreeTermsRequired"));
    setBusy(true);
    try {
      const body = mode === "signup"
        ? { ...f, terms_accepted: true, terms_version: "2.0", marketing_opt_in: marketing }
        : { email: f.email, password: f.password };
      const r = await api.post(`/member/${mode === "signup" ? "signup" : "login"}`, body);
      setMember(r.data);
      toast.success(mode === "signup" ? t("memberAccountCreated") : t("loginSuccess"));
      window.location.href = "/member";
    } catch (e) {
      toast.error(err(e, t("loginFailed")));
    } finally { setBusy(false); }
  };

  const forgot = async () => {
    if (!f.email) return toast.error(t("enterEmailFirst"));
    setBusy(true);
    try {
      await api.post("/member/forgot-password", { email: f.email });
      toast.success(t("resetLinkSent"));
    } catch (e) { toast.error(err(e, t("resetLinkFailed"))); }
    finally { setBusy(false); }
  };

  const perks = [[History, t("perkHistory")], [Crown, t("perkTier")], [Gift, t("perkRewards")],
                 [MapPin, t("perkAddresses")]];

  return (
    <SiteLayout>
      <div className="mx-auto max-w-md px-5 py-16 lm-fade" data-testid="member-login">
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">
          {mode === "signup" ? t("memberSignUpTitle") : t("memberLoginTitle")}
        </h1>
        <p className="mt-3 text-sm text-ink-soft">
          {mode === "signup"
            ? t("memberSignUpSub")
            : t("memberLoginSub")}
        </p>

        {mode === "login" && (
          <ul className="mt-8 space-y-3">
            {perks.map(([Icon, label]) => (
              <li key={label} className="flex items-start gap-3 text-sm">
                <Icon size={16} className="mt-0.5 text-lm-gold shrink-0" /> {label}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-8 space-y-3">
          {mode === "signup" && (
            <>
              <Field label={t("username")} testid="signup-username" value={f.username}
                onChange={set("username")} placeholder={t("usernamePlaceholder")} autoComplete="username" />
              <Field label={t("whatsapp")} testid="signup-whatsapp" value={f.whatsapp}
                onChange={set("whatsapp")} placeholder="08xx / 62xx" inputMode="tel" />
            </>
          )}
          <Field label="Email" testid={`${mode}-email`} type="email" value={f.email}
            onChange={set("email")} placeholder="nama@email.com" autoComplete="email" />
          <Field label={t("password")} testid={`${mode}-password`} type="password" value={f.password}
            onChange={set("password")} placeholder={mode === "signup" ? t("passwordMinPlaceholder") : "••••••••"}
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            onKeyDown={(e) => e.key === "Enter" && submit()} />
          {mode === "signup" && (
            <Field label={t("confirmPassword")} testid="signup-password-confirmation" type="password"
              value={f.password_confirmation} onChange={set("password_confirmation")}
              placeholder={t("repeatPassword")} autoComplete="new-password" />
          )}
        </div>

        {mode === "signup" && (
          <div className="mt-5 space-y-3">
            <label className="flex items-start gap-3 text-xs">
              <input type="checkbox" data-testid="signup-terms" checked={agree} className="mt-0.5"
                onChange={(e) => setAgree(e.target.checked)} />
              <span>{t("signupTermsPrefix")} {" "}
                <Link to="/syarat-dan-ketentuan" target="_blank" data-testid="signup-terms-link"
                  className="text-lm-red underline">{t("rentalTerms")}</Link>{" "}
                {t("signupTermsSuffix")}
              </span>
            </label>
            <label className="flex items-start gap-3 text-xs text-ink-soft">
              <input type="checkbox" data-testid="signup-marketing" checked={marketing} className="mt-0.5"
                onChange={(e) => setMarketing(e.target.checked)} />
              <span>{t("marketingOptIn")}</span>
            </label>
          </div>
        )}

        <button data-testid={`member-${mode}-submit`} disabled={busy || (mode === "signup" && !agree)}
          onClick={submit}
          className="mt-6 w-full h-12 rounded-full lm-btn-primary text-sm font-semibold disabled:opacity-60">
          {busy ? t("processing") : mode === "signup" ? t("createAccount") : t("signIn")}
        </button>

        <div className="mt-4 flex items-center justify-between text-xs">
          <button data-testid="member-mode-toggle" className="text-lm-red underline"
            onClick={() => setMode(mode === "signup" ? "login" : "signup")}>
            {mode === "signup" ? t("alreadyHaveAccount") : t("noAccount")}
          </button>
          {mode === "login" && (
            <button data-testid="member-forgot" onClick={forgot} className="text-ink-soft hover:text-lm-red">
              {t("forgotPassword")}
            </button>
          )}
        </div>

        <p className="mt-4 text-xs text-ink-soft">{t("guestNote")}</p>
      </div>
    </SiteLayout>
  );
}
